import React, { useEffect, useMemo, useRef } from "react";

import Box from "@mui/material/Box";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import { makeStyles } from "tss-react/mui";

import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Switch from "../../../../../Common/Switch";
import DraggableTable from "../../../../../Common/DraggableTable";
import { DATA_SOURCE } from "../../../../../../util/Data/DataModeling";

const survivorshipArr = ["Source-based", "Frequency", "Recency", "Maximum", "Minimum"];
const SurvivorshipStrategyMessage = {
  "Source-based": `Winner is picked based on ${DATA_SOURCE.singular} priority`,
  Frequency: "Winner determined based on # of occurrences of the same value",
  Recency: "Winner is based on recency of the data value",
  Maximum: "Winner is based on value, applies to numeric only",
  Minimum: "Winner is based on value, applies to numeric only",
};

const valueTypeArr = [
  { key: "Select Value Type", value: "Select Value Type" },
  { key: "String", value: "String" },
  { key: "Short String", value: "ShortString" },
  // { key: "Text", value: "Text" },
  { key: "Set", value: "Set" },
  // { key: "Exact", value: "Exact" },
  // { key: "Exists", value: "Exists" },
  { key: "Address", value: "Address" },
  { key: "Name", value: "Name" },
];
const missingValueArr = [
  { key: "Select Missing Value", value: "Select Missing Value" },
  { key: "True", value: "True" },
  { key: "False", value: "False" },
];

const useStyles = makeStyles()((theme) => ({
  container: {
    width: "100%",
    height: "auto",
    marginTop: "30px",
    "& .MuiPaper-root": {
      marginTop: "9px",
      alignItems: "center",
      borderRadius: 10,
    },
    "& .MuiFormControl-root": { color: "#a2a2a2" },
    "& .MuiAlert-icon": {
      padding: 0,
      backgroundColor: "#67A7D5",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "9px",
      height: "71px",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        height: "100%",
        color: "#FFFFFF",
      },
    },
    "& .MuiAlert-message": {
      padding: "15px 5px",
      color: "#A2A2A2",
    },
  },
  Typography: {
    marginTop: "15px",
    marginBottom: "0",
    color: "#df672a",
    fontSize: "16px",
  },
  DataTableContainer: {
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    padding: "5px 0 0",
    overflowX: "auto",
    "& .ant-table-cell": {
      backgroundColor: "#FFFFFF",
    },
  },
  TextCenter: {
    textAlign: "center !important",
  },
  DragHandle: {
    cursor: "move !important",
    color: "#000000de",
    "&:active": {
      color: "#096dd9",
    },
    "&:hover": {
      color: "#40a9ff",
    },
    "&:active,:focus,:hover": {
      outline: 0,
      textDecoration: "none",
    },
  },
}));

export default function DataMaster({ Settings, mdmFormData }) {
  const { classes } = useStyles();
  const mdmTableRef = useRef();
  const fileTypeRef = useRef(null);

  const {
    survivorShipPriority,
    setSurvivorShipPriority,
    dataSourceList,
    setDataSourceList,
    useForDeduplication,
    setUseForDeduplication,
    nullPriority,
    setNullPriority,
    valueType,
    setValueType,
    missingValue,
    setMissingValue,
  } = mdmFormData;

  const survivorshipList = useMemo(
    () =>
      ["Integer", "Float", "Number"].includes(fileTypeRef.current)
        ? survivorshipArr
        : survivorshipArr.filter((_type) => _type !== "Maximum" && _type !== "Minimum"),
    [fileTypeRef.current]
  );

  const columns = useMemo(
    () => [
      {
        dataIndex: "drag",
        title: "",
        render: () => (
          <span className={classes.DragHandle}>
            <MenuOutlined />
          </span>
        ),
        className: classes.TextLeft,
        width: 46,
      },
      {
        dataIndex: "key",
        title: "Priority",
        className: classes.TextLeft,
        width: 100,
      },
      {
        dataIndex: "Data_Source",
        title: DATA_SOURCE.singular,
        className: classes.TextLeft,
        width: 100,
      },
    ],
    [classes.DragHandle, classes.TextLeft]
  );

  const onChangeEvent = (data) => {
    const { action, dataSource } = data;
    if (action === "drag")
      setDataSourceList(() =>
        dataSource.map(({ key, ...other }) => {
          return { key, ...other };
        })
      );
  };

  useEffect(() => {
    if (Settings.currentField) {
      Settings.dataTableRef.current.getData().forEach((field) => {
        if (field.name === Settings.currentField) {
          fileTypeRef.current = field.type;
          //Prepare table with mapped data sources
          if (!dataSourceList.length)
            field.mappings.forEach((ds, index) => {
              setDataSourceList((Prev) => [
                ...Prev,
                {
                  key: index + 1,
                  Data_Source: ds.dataSource,
                },
              ]);
            });

          //set selected values while edit MDM
          if (field.hasOwnProperty("mdm") && Object.keys(field.mdm).length) {
            setSurvivorShipPriority(field.mdm.survivorship);
            setNullPriority(!!field.mdm.nullPriority);

            if (
              field.hasOwnProperty("mdm") &&
              Object.keys(field.mdm).length &&
              field.mdm.hasOwnProperty("priority") &&
              field.mdm.priority.length
            ) {
              let tempDSMappingList = [];
              field.mdm.priority.forEach((priorObj, key) => {
                tempDSMappingList.push({
                  key: key + 1,
                  Data_Source: priorObj,
                });
              });
              setDataSourceList(tempDSMappingList);
            }
          }

          if (field?.dedupe && Object.keys(field.dedupe).length) {
            setUseForDeduplication(true);
            setValueType(field.dedupe?.valueType || "Select Value Type");
            setMissingValue(field.dedupe?.missingValue || "Select Missing Value");
          }
        }
      });
    }
  }, [
    Settings.currentField,
    Settings.dataTableRef,
    dataSourceList.length,
    setDataSourceList,
    setMissingValue,
    setNullPriority,
    setSurvivorShipPriority,
    setUseForDeduplication,
    setValueType,
  ]);

  return (
    <Box className={classes.container} role="presentation">
      {dataSourceList.length > 1 && (
        <>
          <Typography className={classes.Typography} variant="subtitle2" gutterBottom component="div">
            Mastering Rules (MDM)
          </Typography>
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Survivorship Strategy</InputLabel>
            <Select
              value={survivorShipPriority}
              disabled={!dataSourceList.length}
              onChange={(e) => setSurvivorShipPriority(e.target.value)}
              label="Survivorship Strategy"
            >
              {survivorshipList.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack className="switch" sx={{ width: "100%", marginBottom: "20px" }} spacing={2}>
            <Alert
              icon={<InfoOutlinedIcon sx={{ margin: "10px", color: "#67A7D5" }} fontSize="inherit" />}
              sx={{
                backgroundColor: "#ECF7FF",
                border: "1px solid #67A7D5",
                padding: "0",
                marginRight: "2px",
              }}
              variant="outlined"
              severity="info"
            >
              {SurvivorshipStrategyMessage[survivorShipPriority]}
            </Alert>
          </Stack>
          <Typography className={classes.Typography} variant="subtitle2" gutterBottom component="div">
            {`${DATA_SOURCE.singular} Priority`}
          </Typography>
          <DraggableTable
            className={classes.DataTableContainer}
            data={dataSourceList}
            column={columns}
            rowSelection={false}
            onChange={onChangeEvent}
            ref={mdmTableRef}
          />
          <Typography className={classes.Typography} variant="subtitle2" gutterBottom component="div">
            Handle NULL values
          </Typography>
          <FormControl component="fieldset">
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={nullPriority}
                control={<Checkbox sx={{ marginLeft: "45px" }} />}
                label="Consider null values for survivorship"
                labelPlacement="start"
                sx={{ margin: "0" }}
                onChange={() => setNullPriority(!nullPriority)}
                checked={nullPriority}
              />
            </FormGroup>
          </FormControl>
        </>
      )}
      <Typography className={classes.Typography} sx={{ mt: "5px" }} variant="subtitle2" gutterBottom component="div">
        Deduplication
      </Typography>
      <FormControl component="fieldset">
        <FormGroup aria-label="position" row>
          <FormControlLabel
            value={useForDeduplication}
            control={<Switch sx={{ marginLeft: "45px" }} />}
            label="Use for Deduplication"
            labelPlacement="start"
            sx={{ margin: "0" }}
            onChange={() => setUseForDeduplication(!useForDeduplication)}
            checked={useForDeduplication}
          />
        </FormGroup>
      </FormControl>
      {useForDeduplication && (
        <>
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Value Type</InputLabel>
            <Select value={valueType} onChange={(e) => setValueType(e.target.value)} label="Value Type">
              {valueTypeArr.map((option) => (
                <MenuItem key={option.key} value={option.value} disabled={option.value === "Select Value Type"}>
                  {option.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Missing Value</InputLabel>
            <Select value={missingValue} onChange={(e) => setMissingValue(e.target.value)} label="Missing Value">
              {missingValueArr.map((option) => (
                <MenuItem key={option.key} value={option.value} disabled={option.value === "Select Missing Value"}>
                  {option.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
    </Box>
  );
}
